/* eslint-disable react/prop-types */
import React, { useRef, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import { Typography, useMediaQuery, makeStyles } from "@material-ui/core";
import { PriceIn, PriceOut } from "resources/constants";
import { useSelector } from "react-redux";
import moment from "moment";
import _ from "lodash";
import { BsArrowUp } from "react-icons/bs";

const useStyles = makeStyles(() => ({
  arrowPercentage: {
    color: "#939393",
    marginRight: "1px",
  },
  containerRow: {
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
  },
}));

function PassthroughChartComponent({ data, historicsDataYoY }) {
  const refs = useRef();
  const classes = useStyles();
  const metadataSlice = useSelector((state) => state.metadata);
  const [tooltipProps, setTooltipProps] = useState({});
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";
  const priceInLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceIn
    : "Price In";

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "fixed",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const getHistoricValue = (
    historicData,
    historicDate,
    valueNumber,
    typeDate
  ) => {
    const historicValue = _.find(historicData, (item) => {
      return (
        moment(item.date).format("MMM'YY") ===
        moment(historicDate)
          .subtract(valueNumber, typeDate)
          .format("MMM'YY")
      );
    });

    return historicValue;
  };

  const getHistoricValueForYoY = (historicDate) => {
    return getHistoricValue(historicsDataYoY, historicDate, 1, "years");
  };

  const getHistoricValueForMoM = (historicDate, historicsDataMoM) => {
    let previousMonthHistoricValue = getHistoricValue(
      historicsDataMoM,
      historicDate,
      1,
      "months"
    );

    if (!previousMonthHistoricValue) {
      previousMonthHistoricValue = getHistoricValue(
        historicsDataYoY,
        historicDate,
        1,
        "months"
      );
    }

    return previousMonthHistoricValue;
  };

  const getDifferenceYoyOrMom = (value, historicValue) => {
    return _.round((100 * (value - historicValue)) / historicValue, 1);
  };

  const chartOptions = {
    scales: {
      xAxes: [
        {
          id: "Time",
          offset: true,
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: isSmallScreen ? 45 : 0,
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: 0,
            minRotation: 0,
          },
        },
      ],
    },
    layout: {
      padding: {
        left: 10,
        right: 15,
        top: 15,
        bottom: 15,
      },
    },
    legend: {
      display: false,
    },
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "nearest",
      axis: "x",
      custom: useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }

        const chart = refs.current.chartInstance;
        const { canvas } = chart.ctx;

        if (canvas) {
          const position = canvas.getBoundingClientRect();

          // set position of tooltip
          const left =
            tooltipModel.caretX <= chart.width / 2
              ? position.left + tooltipModel.caretX + 40
              : position.left + tooltipModel.caretX - 220;
          const top =
            tooltipModel.caretY <= chart.height / 2
              ? position.top + tooltipModel.caretY - 20
              : position.top + tooltipModel.caretY - 140;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <>
      {tooltipProps.showTooltip ? (
        <CustomTooltip
          tooltipProps={tooltipProps}
          items={[
            refs.current.chartInstance.data.datasets[
              tooltipProps.tooltipModel.dataPoints[0].datasetIndex
            ].data[tooltipProps.tooltipModel.dataPoints[0].index].price
              .absolute && {
              content: (
                <div>
                  <Typography variant="body2">
                    {priceOutLabelByTenant}
                  </Typography>
                  <Typography variant="caption">
                    {`${
                      metadataSlice.all.margin[0].unit
                    } ${refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[
                      tooltipProps.tooltipModel.dataPoints[0].index
                    ].price.absolute.toFixed(2)}`}
                  </Typography>
                  {getHistoricValueForYoY(
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index].date
                  ) &&
                    getDifferenceYoyOrMom(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                        .price.absolute,
                      getHistoricValueForYoY(
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                          .date
                      ).avg_weight_by_units_price
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price.absolute,
                                getHistoricValueForYoY(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date
                                ).avg_weight_by_units_price
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price.absolute,
                                getHistoricValueForYoY(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date
                                ).avg_weight_by_units_price
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            refs.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index
                            ].price.absolute,
                            getHistoricValueForYoY(
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index
                              ].date
                            ).avg_weight_by_units_price
                          )
                        )}% YoY`}
                      </div>
                    )}

                  {getHistoricValueForMoM(
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index].date,
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data
                  ) &&
                    getDifferenceYoyOrMom(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                        .price.absolute,
                      getHistoricValueForMoM(
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                          .date,
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data
                      ).avg_weight_by_units_price
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price.absolute,
                                getHistoricValueForMoM(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date,
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data
                                ).avg_weight_by_units_price
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price.absolute,
                                getHistoricValueForMoM(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date,
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data
                                ).avg_weight_by_units_price
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            refs.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index
                            ].price.absolute,
                            getHistoricValueForMoM(
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index
                              ].date,
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data
                            ).avg_weight_by_units_price
                          )
                        )}% MoM`}
                      </div>
                    )}
                </div>
              ),
              borderStyle: {
                borderColor:
                  refs.current.chartInstance.data.datasets[0].backgroundColor,
              },
            },
            refs.current.chartInstance.data.datasets[
              tooltipProps.tooltipModel.dataPoints[0].datasetIndex
            ].data[tooltipProps.tooltipModel.dataPoints[0].index].price_in
              .absolute && {
              content: (
                <div>
                  <Typography variant="body2">
                    {priceInLabelByTenant}
                  </Typography>
                  <Typography variant="caption">
                    {`${
                      metadataSlice.all.margin[0].unit
                    } ${refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[
                      tooltipProps.tooltipModel.dataPoints[0].index
                    ].price_in.absolute.toFixed(2)}`}
                  </Typography>
                  {getHistoricValueForYoY(
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index].date
                  ) &&
                    getDifferenceYoyOrMom(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                        .price_in.absolute,
                      getHistoricValueForYoY(
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                          .date
                      ).avg_weight_by_units_price_in
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price_in.absolute,
                                getHistoricValueForYoY(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date
                                ).avg_weight_by_units_price_in
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price_in.absolute,
                                getHistoricValueForYoY(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date
                                ).avg_weight_by_units_price_in
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            refs.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index
                            ].price_in.absolute,
                            getHistoricValueForYoY(
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index
                              ].date
                            ).avg_weight_by_units_price_in
                          )
                        )}% YoY`}
                      </div>
                    )}

                  {getHistoricValueForMoM(
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index].date,
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data
                  ) &&
                    getDifferenceYoyOrMom(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                        .price_in.absolute,
                      getHistoricValueForMoM(
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                          .date,
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data
                      ).avg_weight_by_units_price_in
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price_in.absolute,
                                getHistoricValueForMoM(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date,
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data
                                ).avg_weight_by_units_price_in
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].price_in.absolute,
                                getHistoricValueForMoM(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date,
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data
                                ).avg_weight_by_units_price_in
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            refs.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index
                            ].price_in.absolute,
                            getHistoricValueForMoM(
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index
                              ].date,
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data
                            ).avg_weight_by_units_price_in
                          )
                        )}% MoM`}
                      </div>
                    )}
                </div>
              ),
              borderStyle: {
                borderStyle: "dashed",
                borderColor:
                  refs.current.chartInstance.data.datasets[1].backgroundColor,
              },
            },
            refs.current.chartInstance.data.datasets[
              tooltipProps.tooltipModel.dataPoints[0].datasetIndex
            ].data[tooltipProps.tooltipModel.dataPoints[0].index].cost
              .absolute && {
              content: (
                <div>
                  <Typography variant="body2">Cost</Typography>
                  <Typography variant="caption">
                    {`${
                      metadataSlice.all.margin[0].unit
                    } ${refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[
                      tooltipProps.tooltipModel.dataPoints[0].index
                    ].cost.absolute.toFixed(2)}`}
                  </Typography>
                  {getHistoricValueForYoY(
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index].date
                  ) &&
                    getDifferenceYoyOrMom(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index].cost
                        .absolute,
                      getHistoricValueForYoY(
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                          .date
                      ).avg_weight_by_units_cost
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].cost.absolute,
                                getHistoricValueForYoY(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date
                                ).avg_weight_by_units_cost
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].cost.absolute,
                                getHistoricValueForYoY(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date
                                ).avg_weight_by_units_cost
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            refs.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index
                            ].cost.absolute,
                            getHistoricValueForYoY(
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index
                              ].date
                            ).avg_weight_by_units_cost
                          )
                        )}% YoY`}
                      </div>
                    )}

                  {getHistoricValueForMoM(
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index].date,
                    refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data
                  ) &&
                    getDifferenceYoyOrMom(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index].cost
                        .absolute,
                      getHistoricValueForMoM(
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                          .date,
                        refs.current.chartInstance.data.datasets[
                          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                        ].data
                      ).avg_weight_by_units_cost
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].cost.absolute,
                                getHistoricValueForMoM(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date,
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data
                                ).avg_weight_by_units_cost
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                refs.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data[
                                  tooltipProps.tooltipModel.dataPoints[0].index
                                ].cost.absolute,
                                getHistoricValueForMoM(
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                                  ].date,
                                  refs.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data
                                ).avg_weight_by_units_cost
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            refs.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index
                            ].cost.absolute,
                            getHistoricValueForMoM(
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index
                              ].date,
                              refs.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data
                            ).avg_weight_by_units_cost
                          )
                        )}% MoM`}
                      </div>
                    )}
                </div>
              ),
              borderStyle: {
                borderColor:
                  refs.current.chartInstance.data.datasets[2].backgroundColor,
              },
            },
          ]}
          tooltipData={{
            title: `Price - ${
              refs.current.chartInstance.data.datasets[
                tooltipProps.tooltipModel.dataPoints[0].datasetIndex
              ].data[tooltipProps.tooltipModel.dataPoints[0].index].x
            }`,
          }}
        />
      ) : null}
      <Line ref={refs} data={data} options={chartOptions} />
    </>
  );
}

export default PassthroughChartComponent;
